class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      setupGiftOrderCheckbox();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

async function updateCartAttribute(attributes) {
  try {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ attributes })
    });

    if (!res.ok) {
      throw new Error(`update.js returned ${res.status}`);
    }

    return await res.json();
  } catch (err) {
    // Caller can retry
    console.error('Failed to update cart attribute:', err);
    throw err;
  }
}

async function setupGiftOrderCheckbox() {
  const checkbox = document.querySelector('#gift_order');
  if (!checkbox) return;

  const sessionGiftOrder = sessionStorage.getItem('giftOrder'); // 'yes' | 'no' | null

  // If session explicitly says 'no' -> respect that immediately
  if (sessionGiftOrder === 'no') {
    checkbox.checked = false;
  } else if (sessionGiftOrder === 'yes') {
    // If session explicitly says 'yes' -> respect that immediately
    checkbox.checked = true;
  } else {
    // No session decision yet -> ask cart for authoritative value
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (res.ok) {
        const cart = await res.json();
        const giftOrderAttr = cart.attributes?.['Gift Order'];
        checkbox.checked = giftOrderAttr === 'true';
      } else {
        // If cart read fails, default to unchecked (or change to true if you prefer)
        console.error('Failed to read cart.js', res.status);
        checkbox.checked = false;
      }
    } catch (err) {
      console.error('Error fetching /cart.js', err);
      checkbox.checked = false;
    }
  }

  // Handle user toggling the checkbox
  checkbox.addEventListener('change', async (e) => {
    const checked = e.target.checked;

    if (checked) {
      // User chose gift -> record session intent and write attribute
      sessionStorage.setItem('giftOrder', 'yes');

      // Try to set attribute, retry once on failure
      try {
        await updateCartAttribute({ 'Gift Order': 'true' });
      } catch (err) {
        // retry once
        try {
          await updateCartAttribute({ 'Gift Order': 'true' });
        } catch (err2) {
          console.error('Failed to save Gift Order attribute after retry', err2);
          // Keep session flag so user intent is preserved until they reload; optionally show UI error
        }
      }
    } else {
      // User removed gift -> record session intent and remove attribute
      sessionStorage.setItem('giftOrder', 'no');

      try {
        // remove attribute by setting it to empty string
        await updateCartAttribute({ 'Gift Order': '' });
      } catch (err) {
        // retry once
        try {
          await updateCartAttribute({ 'Gift Order': '' });
        } catch (err2) {
          console.error('Failed to clear Gift Order attribute after retry', err2);
        }
      }
    }
  });
}

document.addEventListener('DOMContentLoaded', setupGiftOrderCheckbox);